import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import ConfirmationModal from "../../components/Modals/ConfirmationModal";
import PipelineStepMappingModal from "../../components/services/PipelineStepMappingModal";
import { getJobs } from "../../services/jobs/getJobs";
import { previewPipelineChange } from "../../services/service/previewPipelineChange";
import { getServiceDepartments } from "../../services/service/getServiceDepartments";
import { getServiceV2 } from "../../services/service/getServiceV2";
import { getServiceTasksByJob } from "../../services/serviceTasks/getServiceTasksByJob";
import { updateService } from "../../services/updateService";
import DepartmentListbox from "../TaskForm/DepartmentListBox";
import JobsListBox from "../TaskForm/JobsListBox";
import { getNewLoggedInEmployeeFunction } from "../../helpers/getLoggedInEmployeeFunction";
import TypeBadge from "../../components/TypeBadge";
import { term } from "../../terms";

function EditServiceForm() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const serviceId = useParams().id;
    const navigate = useNavigate();
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState({});
    const [jobs, setJobs] = useState([]);
    const [selectedJob, setSelectedJob] = useState({ name: null });
    const [originalJob, setOriginalJob] = useState({ name: null });
    const [pendingJob, setPendingJob] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [stepMappings, setStepMappings] = useState([]);
    const [requiresMapping, setRequiresMapping] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
    const [showNoInProgressConfirm, setShowNoInProgressConfirm] = useState(false);
    const [noInProgressPreview, setNoInProgressPreview] = useState(null);
    const [pendingNoInProgressJob, setPendingNoInProgressJob] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [loggedInEmployee, setLoggedInEmployee] = useState(null);

    /* const [service, setService] = useState({
        name: "",
    }); */
    const [form, setForm] = useState({
        name: "",
        department: null,
        isEfileStatusActive: false,
    });

    const getNewDepartments = async () => {
        const response = await getServiceDepartments();
        if (response.success) {
            setDepartments(response.data.departments);
            //setSelectedDepartment(response.data.departments[0]);
        } else {
            toast.error(response.data.message);
        }
    };

    const getNewJobs = async () => {
        const response = await getJobs();
        if (response.success) {
            setJobs((response.data.jobs || []).filter((j) => !j.isDeleted));
        } else {
            toast.error(response.data.message);
        }
    };

    const getNewTasks = async (jobId) => {
        const response = await getServiceTasksByJob(jobId);
        if (response.success) {
            setTasks(response.data);
        } else {
            toast.error(response.message);
        }
    };

    useEffect(() => {
        async function runOnLoad() {
            if (typeof serviceId === "undefined") return;
            setLoading(true);
            setPendingJob(null);
            setPreviewData(null);
            setStepMappings([]);
            setRequiresMapping(false);
            setIsMappingModalOpen(false);
            setIsPreviewLoading(false);
            setShowNoInProgressConfirm(false);
            setNoInProgressPreview(null);
            setPendingNoInProgressJob(null);

            const service = await getServiceV2(serviceId);
            console.log("servicio", service);
            if (service.success === false) return navigate("/departments");
            console.log(service);
            setForm({
                name: service.data.name,
                isEfileStatusActive: service.data.isEfileStatusActive,
            });

            /* setService({
                name: service.data.name,
            }); */

            setSelectedDepartment(service.data.department);
            setSelectedJob(service.data.job);
            setOriginalJob(service.data.job);
            setStepMappings([]);
            getNewTasks(service.data.job.id);
            setLoading(false);
        }
        getNewDepartments();
        getNewJobs();
        runOnLoad();

        // eslint-disable-next-line
    }, [serviceId]);

    useEffect(() => {
        getNewLoggedInEmployeeFunction(setLoggedInEmployee);
    }, []);

    const pluralize = (word, n) => (n === 1 ? word : `${word}s`);
    const buildPipelineChangeToast = (data) => {
        const toDo = Number(data?.toDoMigrated ?? 0);
        const inProg = Number(data?.inProgressMigrated ?? 0);
        const complete = Number(data?.completeUntouched ?? 0);
        const active = toDo + inProg;

        const parts = [];
        if (active > 0) {
            parts.push(
                `${active} active ${pluralize("service", active)} ${
                    active === 1 ? "was" : "were"
                } updated`,
            );
        }
        if (complete > 0) {
            parts.push(
                `${complete} completed ${pluralize("service", complete)} ${
                    complete === 1 ? "was" : "were"
                } left unchanged`,
            );
        }

        if (parts.length === 0) return "Pipeline updated successfully.";
        return `Pipeline updated successfully. ${parts.join(". ")}.`;
    };

    const getApiErrorMessage = (err, fallback = "Something went wrong") =>
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.data?.message ||
        err?.message ||
        fallback;

    const applyServiceUpdate = async ({ jobId, mappings }) => {
        const trimmedServiceName = form.name.trim();
        if (!loggedInEmployee || loggedInEmployee.role === 0) {
            toast.error("You don't have permission to do this.");
            return;
        }
        if (trimmedServiceName === "") {
            toast.error("Name can't be empty");
            return;
        }
        if (!jobId) {
            toast.error("You must select a step");
            return;
        }

        setIsApplying(true);
        try {
            const res = await updateService({
                id: serviceId,
                name: trimmedServiceName,
                isEfileStatusActive: form.isEfileStatusActive,
                departmentId: selectedDepartment.id,
                jobId,
                stepMappings: mappings || [],
            });

            if (res?.data?.success) {
                if (res.data.wasPipelineChanged) {
                    toast.success(buildPipelineChangeToast(res.data));
                } else {
                    toast.success(res.data.message || "Service updated.");
                }

                if (res.data.wasPipelineChanged && res.data.newServiceId) {
                    navigate(`/services/${res.data.newServiceId}`, {
                        replace: true,
                    });
                    return;
                }
            } else {
                toast.error(getApiErrorMessage(res));
            }
        } catch (err) {
            toast.error(getApiErrorMessage(err));
        } finally {
            setIsApplying(false);
        }
    };

    const saveDetails = async () => {
        const trimmedServiceName = form.name.trim();
        if (!loggedInEmployee || loggedInEmployee.role === 0) {
            toast.error("You don't have permission to do this.");
            return;
        }

        if (trimmedServiceName == "") {
            toast.error("Name can't be empty");
            return;
        }
        if (selectedJob.name == null) {
            toast.error("You must select a step");
            return;
        }

        const isPipelineChanged = selectedJob.id !== originalJob.id;
        if (isPipelineChanged && requiresMapping && stepMappings.length === 0) {
            toast.error("Confirm step mapping to proceed.");
            return;
        }

        await applyServiceUpdate({
            jobId: selectedJob.id,
            mappings: isPipelineChanged ? stepMappings : [],
        });
    };

    const onHandleChangeInput = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };

    const onHandleChangeDepartment = (e) => {
        setSelectedDepartment(e);
    };

    const buildNoMatchMappings = (oldSteps = []) => {
        return oldSteps.map((s) => ({
            oldServiceTaskId: s.id,
            newServiceTaskId: null,
        }));
    };

    const onHandleChangeJob = async (newJob) => {
        if (!newJob?.id) return;

        if (newJob.id === originalJob.id) {
            setSelectedJob(originalJob);
            setPendingJob(null);
            setPreviewData(null);
            setStepMappings([]);
            setRequiresMapping(false);
            setIsMappingModalOpen(false);
            setShowNoInProgressConfirm(false);
            setNoInProgressPreview(null);
            setPendingNoInProgressJob(null);
            getNewTasks(originalJob.id);
            return;
        }

        setIsPreviewLoading(true);
        const previewResponse = await previewPipelineChange({
            serviceId,
            newJobId: newJob.id,
        });
        setIsPreviewLoading(false);

        if (!previewResponse.success || previewResponse.data?.success === false) {
            toast.error(
                previewResponse.message ||
                    previewResponse.data?.message ||
                    "Unable to preview pipeline change.",
            );
            setSelectedJob(originalJob);
            setPendingJob(null);
            setPreviewData(null);
            setStepMappings([]);
            setIsMappingModalOpen(false);
            setShowNoInProgressConfirm(false);
            setNoInProgressPreview(null);
            setPendingNoInProgressJob(null);
            return;
        }

        const preview = previewResponse.data;
        setPendingJob(newJob);
        setPreviewData(preview);

        if ((preview?.inProgressCount ?? 0) === 0) {
            const autoMappings = buildNoMatchMappings(preview?.oldSteps || []);
            if (autoMappings.length === 0) {
                toast.error(
                    "Current pipeline has no steps. Cannot change pipeline.",
                );
                setSelectedJob(originalJob);
                setPendingJob(null);
                setPreviewData(null);
                setStepMappings([]);
                setRequiresMapping(false);
                setIsMappingModalOpen(false);
                return;
            }

            setStepMappings(autoMappings);
            setRequiresMapping(false);
            setIsMappingModalOpen(false);
            setPendingJob(null);
            setPreviewData(null);
            setPendingNoInProgressJob(newJob);
            setNoInProgressPreview(preview);
            setShowNoInProgressConfirm(true);
            return;
        }

        setStepMappings([]);
        setRequiresMapping(true);
        setIsMappingModalOpen(true);
    };

    const onCancelPipelineMapping = () => {
        setIsMappingModalOpen(false);
        setSelectedJob(originalJob);
        setPendingJob(null);
        setPreviewData(null);
        setStepMappings([]);
        setRequiresMapping(false);
        setShowNoInProgressConfirm(false);
        setNoInProgressPreview(null);
        setPendingNoInProgressJob(null);
        if (originalJob?.id) {
            getNewTasks(originalJob.id);
        }
    };

    const onCancelNoInProgressPipelineChange = () => {
        setShowNoInProgressConfirm(false);
        setNoInProgressPreview(null);
        setPendingNoInProgressJob(null);
        setPendingJob(null);
        setPreviewData(null);
        setSelectedJob(originalJob);
        if (originalJob?.id) {
            getNewTasks(originalJob.id);
        }
    };

    const onConfirmNoInProgressPipelineChange = () => {
        const confirmedJobId = pendingNoInProgressJob?.id;
        const mappings = stepMappings;

        if (pendingNoInProgressJob?.id) {
            setSelectedJob(pendingNoInProgressJob);
            getNewTasks(pendingNoInProgressJob.id);
        }
        setShowNoInProgressConfirm(false);
        setNoInProgressPreview(null);
        setPendingNoInProgressJob(null);
        setPendingJob(null);
        setPreviewData(null);

        if (confirmedJobId) {
            applyServiceUpdate({ jobId: confirmedJobId, mappings });
        }
    };

    const onConfirmPipelineMapping = (mappings) => {
        setStepMappings(mappings);
        setIsMappingModalOpen(false);
        setPreviewData(null);

        if (pendingJob?.id) {
            setSelectedJob(pendingJob);
            getNewTasks(pendingJob.id);
            applyServiceUpdate({ jobId: pendingJob.id, mappings });
        }
        setPendingJob(null);
    };

    const isPipelineChanged =
        selectedJob?.id && originalJob?.id && selectedJob.id !== originalJob.id;
    const isSaveDisabled =
        isApplying ||
        isPreviewLoading ||
        (isPipelineChanged && requiresMapping && stepMappings.length === 0);

    return (
        <section>
            <Toaster />
            <ConfirmationModal
                showDialog={showNoInProgressConfirm}
                onCancel={onCancelNoInProgressPipelineChange}
                onConfirm={onConfirmNoInProgressPipelineChange}
                title="Confirm pipeline change"
                description={`${noInProgressPreview?.toDoCount ?? 0} To Do services will be reset to the new pipeline.`}
            />
            <div className="flex h-screen overflow-hidden">
                {/* Sidebar */}
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                {/* Content area */}
                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    {/*  Site header */}
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    {!loading ? (
                        <main>
                            <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                                <div className="flex flex-col items-start max-w-2xl">
                                    <Breadcrumbs
                                        home="/departments"
                                        pages={[
                                            {
                                                name: "Services",
                                                href: `/templates/services`,
                                                current: true,
                                            },
                                            {
                                                name: "Edit service",
                                                href: `/services/${serviceId}`,
                                                current: true,
                                            },
                                        ]}
                                    />

                                    {/* Page header */}
                                    <div className="w-full mt-12 mb-8 sm:flex sm:justify-between sm:items-center">
                                        {/* Left: Title */}
                                        <div className="mb-4 sm:mb-0">
                                            <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                                Edit <TypeBadge type="service" />
                                            </h1>
                                        </div>
                                    </div>

                                    <div className="relative w-full bg-white border rounded-sm shadow-lg border-slate-200">
                                        <header className="px-5 py-4 border-b border-slate-200">
                                            <h2 className="font-semibold text-slate-800">
                                                Details
                                            </h2>
                                        </header>

                                        <div className="px-5 py-4 space-y-5">
                                            <div>
                                                <label
                                                    htmlFor="name"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Name
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        name="name"
                                                        type="text"
                                                        value={form.name}
                                                        onChange={(e) =>
                                                            onHandleChangeInput(e)
                                                        }
                                                        onBlur={() =>
                                                            setForm((prev) => ({
                                                                ...prev,
                                                                name: prev.name.trim(),
                                                            }))
                                                        }
                                                        className="inline-flex justify-between w-full px-3 py-2 text-base font-normal text-gray-700 truncate bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0"
                                                    />
                                                </div>
                                            </div>
                                            <div className="mt-1">
                                                <DepartmentListbox
                                                    label={"Department"}
                                                    departments={departments}
                                                    selectedDepartment={
                                                        selectedDepartment
                                                    }
                                                    onChangeDepartment={(e) =>
                                                        onHandleChangeDepartment(e)
                                                    }
                                                />
                                                <div className="mt-4">
                                                    <JobsListBox
                                                        label={term(
                                                            "pipeline_singular",
                                                        )}
                                                        items={jobs}
                                                        selectedItem={selectedJob}
                                                        onChangeItem={(e) =>
                                                            onHandleChangeJob(e)
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <label className="flex items-start gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={
                                                        form.isEfileStatusActive
                                                    }
                                                    onChange={(e) =>
                                                        setForm((prev) => ({
                                                            ...prev,
                                                            isEfileStatusActive:
                                                                e.target.checked,
                                                        }))
                                                    }
                                                    className="mt-1 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                                                />
                                                <span>
                                                    <span className="block text-sm font-medium text-gray-700">
                                                        E-file Status applies to client
                                                        services using this template.
                                                    </span>
                                                    <span className="block text-sm text-slate-500">
                                                        Enable this setting when those
                                                        client services require an
                                                        E-file workflow.
                                                    </span>
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="w-full mt-4">
                                        {isPipelineChanged &&
                                            requiresMapping &&
                                            stepMappings.length === 0 && (
                                                <p className="text-sm text-amber-600">
                                                    Confirm step mapping to proceed.
                                                </p>
                                            )}
                                    </div>
                                    <div className="flex justify-end w-full">
                                        <button
                                            disabled={isSaveDisabled}
                                            onClick={() => saveDetails()}
                                            type="submit"
                                            className="z-50 inline-flex justify-center px-4 py-2 mt-4 ml-3 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Save
                                        </button>
                                    </div>

                                    <div className="relative w-full mt-12 bg-white border rounded-sm shadow-lg border-slate-200">
                                        <header className="flex items-center justify-between w-full px-5 py-4 border-b border-slate-200">
                                            <h2 className="font-semibold text-slate-800">
                                                {term("step_plural")} assigned to
                                                current {term("pipeline_singular")}
                                            </h2>
                                            <a
                                                href={`/templates/job/${selectedJob?.id}`}
                                                className="inline-flex items-center px-3 py-1 text-sm font-medium text-teal-600 hover:text-teal-700"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Edit {term("pipeline_singular")}
                                            </a>
                                        </header>
                                        {tasks.map((task) => (
                                            <div
                                                key={task.id}
                                                className="w-full py-2 pl-4 m-2 text-sm"
                                            >
                                                <div className="flex items-center w-full">
                                                    <h1
                                                        htmlFor="comments"
                                                        className="w-full font-medium text-gray-700"
                                                    >
                                                        {task.name}
                                                    </h1>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </main>
                    ) : (
                        <div className="flex items-center justify-center w-full mt-64 max-w-9xl">
                            <motion.div
                                className="border-t-gray-700 border-t-[4px] rounded-full border-gray-200 border-[4px] w-12 h-12"
                                initial={{ rotateZ: 0 }}
                                animate={{ rotateZ: 360 }}
                                exit={{ rotateZ: 0 }}
                                transition={{
                                    repeat: Infinity,
                                    ease: "linear",
                                    duration: 1,
                                }}
                            ></motion.div>
                        </div>
                    )}
                </div>
            </div>
            <PipelineStepMappingModal
                open={isMappingModalOpen}
                setOpen={setIsMappingModalOpen}
                previewData={previewData}
                onConfirm={onConfirmPipelineMapping}
                onCancel={onCancelPipelineMapping}
            />
        </section>
    );
}
export default EditServiceForm;
